import { useEffect, useRef, useState } from 'react'

import { ABIDescription, ABIParameter, IRemixApi } from '@remixproject/plugin-api'
import { PluginApi } from '@remixproject/plugin-utils'
import { PluginClient } from '@remixproject/plugin'
import { BigNumberish, BrowserProvider, Contract, JsonRpcProvider, JsonRpcSigner, parseUnits, Wallet } from 'ethers'
import { useWeb3ModalProvider } from '@web3modal/ethers/react'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'

import { ENCRYPTED_INPUT_TYPES, ENCRYPTED_OUTPUT_TYPES } from '../../utils/constants'
import { executeCall, executeTransaction } from '../../utils/transactions'
import TransactionInput from '../shared/TransactionInput'
import { useEnvironmentStore } from '../../zustand/environmentStore'
import ReturnData from './ReturnData'
import SpinnerButton from '../shared/SpinnerButton'

type ContractInteractionFormProps = {
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>,
    updateBalance: Function,
    functionIdx: string,
    contractName: string,
    contractAddress: string,
    contractAbi: ABIDescription[],
    functionName: string,
    functionInputs: ABIParameter[],
    functionOutputs: ABIParameter[],
    stateMutability: 'pure' | 'view' | 'nonpayable' | 'payable',
    gasLimit: BigNumberish
    value: bigint
}

const ContractInteractionForm = ({ client, updateBalance, functionIdx, contractName, contractAddress, contractAbi, functionName, functionInputs, functionOutputs, stateMutability, gasLimit, value }: ContractInteractionFormProps) => {
    const { environment, publicKey, privateKey, rpcUrl, userKey } = useEnvironmentStore()
    const { walletProvider } = useWeb3ModalProvider()
    const [requiresMpc, setRequiresMpc] = useState(false)
    const inputRefs = useRef<(any | null)[]>([])
    const [res, setRes] = useState<any>()

    useEffect(() => {
        const checkRequiresMpc = (abi: ABIParameter) => {
            if (abi.components) {
                if ('internalType' in abi && ENCRYPTED_INPUT_TYPES.includes((abi.internalType as string).split(' ')[1])) {
                    setRequiresMpc(true)
                    return
                }

                abi.components.forEach((component) => {
                    checkRequiresMpc(component)
                })

                return
            }

            if ('internalType' in abi && ENCRYPTED_OUTPUT_TYPES.includes(abi.internalType as string)) {
                setRequiresMpc(true)
            }
        }

        functionInputs.forEach((input) => {
            checkRequiresMpc(input)
        })
        functionOutputs.forEach((output) => {
            checkRequiresMpc(output)
        })
    }, [])

    const getButtonVariant = (stateMutability: string | undefined) => {
        switch (stateMutability) {
            case 'payable': return 'danger';
            case 'nonpayable': return 'warning';
            case 'view':
            case 'pure': return 'primary';
            default: return 'secondary';
        }
    }

    const executeContractInteraction = async () => {
        const args: any[] = []
        
        try {
    
            for (let i = 0; i < functionInputs.length; i++) {
                args.push(await inputRefs.current[i].handleUpdateValue())
            }
        } catch (e) {
            console.log(e)
            await client.terminal.log({
                type: 'error',
                value: `${e}`
            })

            return
        }

        let signer: JsonRpcSigner | Wallet

        if (environment === 'Wallet') {
            if (walletProvider === undefined) throw Error('Wallet provider is undefined')
    
            const ethersProvider = new BrowserProvider(walletProvider)

            signer = await ethersProvider.getSigner()
        } else if (environment === 'Manual') {
            const provider = new JsonRpcProvider(rpcUrl)

            signer = new Wallet(privateKey, provider)
        } else {
            throw new Error('Unrecognized environment type')
        }

        if (stateMutability === 'payable' || stateMutability === 'nonpayable') {
            await executeTransaction(
                client,
                signer,
                contractName,
                contractAddress,
                contractAbi,
                functionName,
                args,
                value,
                gasLimit
            )

            await updateBalance()

            return
        }

        let res = await executeCall(
            client,
            signer,
            contractName,
            contractAddress,
            contractAbi,
            functionName,
            args
        )

        if (res === '') {
            return
        }
        
        setRes(res)
    }

    const renderTooltip = (props: any) => {
        return (
            <Tooltip id="tooltip-disabled" {...props}>
                Complete the Onboard procedure to use methods with encrypted inputs/outputs
            </Tooltip>
        )
    }

    return (
        <>
            <p className='function-name'>{functionName}</p>

            <Form as='div' key={functionIdx} className='contract-interaction-form'>
                {functionInputs.map((input, inputIdx) => {
                    return  <TransactionInput
                                ref={(el: any) => (inputRefs.current[inputIdx] = el)}
                                contractAddress={contractAddress}
                                functionName={functionName}
                                functionInputs={functionInputs}
                                index={inputIdx}
                                type={input.type}
                                internalType={'internalType' in input ? input['internalType'] as string : input.type}
                                name={input.name}
                            />
                    })}

                
                {requiresMpc && userKey === ''
                    ?   <OverlayTrigger placement='top'  overlay={renderTooltip}>
                            <div>
                                <Button variant={getButtonVariant(stateMutability)} size='sm' type='submit' disabled style={{ pointerEvents: 'none' }}>
                                    {stateMutability === 'view' || stateMutability === 'pure' ? 'call' : 'transact'}
                                </Button>
                            </div>
                        </OverlayTrigger>
                    :   <SpinnerButton
                            disabled={publicKey === ''}
                            handleFunction={executeContractInteraction}
                            size='sm'
                            text={stateMutability === 'view' || stateMutability === 'pure' ? 'call' : 'transact'}
                            variant={getButtonVariant(stateMutability)}
                        />
                        
                }
            </Form>

            <ReturnData res={res} functionOutputs={functionOutputs} />
        </>
    )
}

export default ContractInteractionForm